<template>
  <div v-if="visible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3>新增设备</h3>
        <button @click="handleClose" class="close-btn">&times;</button>
      </div>
      
      <div class="modal-body">
        <form @submit.prevent="handleSubmit" class="device-form">
          <div class="form-group">
            <label for="deviceCode" class="required">设备编码</label>
            <input
              id="deviceCode"
              v-model="formData.deviceCode"
              type="text"
              placeholder="请输入设备编码（同门店不可重复）"
              class="form-input"
              :class="{ 'error': errors.deviceCode }"
              required
            />
            <span v-if="errors.deviceCode" class="error-message">{{ errors.deviceCode }}</span>
          </div>

          <div class="form-group">
            <label for="deviceName" class="required">设备名称</label>
            <input
              id="deviceName"
              v-model="formData.deviceName"
              type="text"
              placeholder="请输入设备名称"
              class="form-input"
              :class="{ 'error': errors.deviceName }"
              required
            />
            <span v-if="errors.deviceName" class="error-message">{{ errors.deviceName }}</span>
          </div>
          
          <div class="form-group">
            <label for="storeId" class="required">所属门店</label>
            <select
              id="storeId"
              v-model="formData.storeId"
              class="form-select"
              :class="{ 'error': errors.storeId }"
              required
            >
              <option value="">请选择门店</option>
              <option v-for="store in storeList" :key="store.id" :value="store.id">
                {{ store.storeName }}
              </option>
            </select>
            <span v-if="errors.storeId" class="error-message">{{ errors.storeId }}</span>
          </div>

          <div class="form-group">
            <label for="deviceType" class="required">设备类型</label>
            <select
              id="deviceType"
              v-model="formData.deviceType"
              class="form-select"
            >
              <option v-for="type in deviceTypeOptions" :key="type.code" :value="type.code">
                {{ type.name }}
              </option>
            </select>
            <div class="form-help">选择“电脑”的类型会绑定客户端 UUID；选择“插座”的类型会在列表显示紫色“插座配置”按钮。</div>
          </div>

          <div class="device-type-manager">
            <div class="manager-header">
              <span>设备类型名称</span>
              <button type="button" class="small-btn" @click="addDeviceTypeOption" :disabled="!formData.storeId">
                新增类型
              </button>
            </div>
            <div v-if="!formData.storeId" class="form-help">请先选择所属门店，再维护该门店的设备类型。</div>
            <div v-else>
              <div v-for="(type, index) in deviceTypeOptions" :key="type.code" class="type-option-row">
                <input
                  v-model="type.name"
                  type="text"
                  class="form-input type-name-input"
                  placeholder="请输入类型名称"
                />
                <label :class="['type-kind-check', { active: type.computerType, muted: type.socketType }]">
                  <input
                    :checked="type.computerType"
                    type="checkbox"
                    @change="setComputerType(type, $event.target.checked)"
                  />
                  电脑
                </label>
                <label :class="['type-kind-check', 'socket', { active: type.socketType, muted: type.computerType }]">
                  <input
                    :checked="type.socketType"
                    type="checkbox"
                    @change="setSocketType(type, $event.target.checked)"
                  />
                  插座
                </label>
                <button type="button" class="remove-type-btn" @click="removeDeviceTypeOption(index)">删除</button>
              </div>
              <button type="button" class="save-type-btn" @click="saveDeviceTypeOptions" :disabled="loadingDeviceTypes">
                {{ loadingDeviceTypes ? '保存中...' : '保存设备类型名称' }}
              </button>
            </div>
          </div>

          <div class="form-group" v-if="formData.deviceType !== '3'">
            <label for="countdownDelayMinutes">延迟开始计时（分钟）</label>
            <input
              id="countdownDelayMinutes"
              v-model.number="formData.countdownDelayMinutes"
              type="number"
              min="0"
              max="99"
              placeholder="为空时跟随门店配置"
              class="form-input"
            />
          </div>
          
          <div class="form-group" v-if="isSelectedComputerType">
            <label for="uuidSearch">系统UUID</label>
            <input
              id="uuidSearch"
              v-model="uuidSearchKeyword"
              type="text"
              class="form-input uuid-search-input"
              placeholder="输入关键字搜索UUID"
            />
            <div v-if="formData.uuid" class="selected-uuid">
              <span>已选择：{{ formData.uuid }}</span>
              <button type="button" @click="clearSelectedUuid">清除</button>
            </div>
            <div v-if="uuidSearchKeyword.trim()" class="uuid-result-list" :class="{ error: errors.uuid }">
              <button
                v-for="item in filteredSystemUuidList"
                :key="item.id"
                type="button"
                :class="['uuid-result-item', { active: item.uuid === formData.uuid }]"
                @click="selectUuid(item.uuid)"
              >
                {{ item.uuid }}
              </button>
              <div v-if="filteredSystemUuidList.length === 0" class="uuid-empty">暂无匹配UUID</div>
            </div>
            <div v-else class="form-help">请输入关键字后选择对应 UUID。</div>
            <span v-if="errors.uuid" class="error-message">{{ errors.uuid }}</span>
          </div>
          
          <div class="form-group">
            <label for="description">设备描述</label>
            <textarea
              id="description"
              v-model="formData.description"
              placeholder="请输入设备描述（可选）"
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>
        </form>
      </div>
      
      <div class="modal-footer">
        <button @click="handleClose" class="cancel-btn">取消</button>
        <button @click="handleSubmit" class="submit-btn" :disabled="loading">
          {{ loading ? '保存中...' : '保存' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'AddDeviceModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    storeList: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      loading: false,
      loadingDeviceTypes: false,
      systemUuidList: [],
      uuidSearchKeyword: '',
      deviceTypeOptions: [],
      formData: {
        deviceCode: '',
        deviceName: '',
        deviceType: '1',
        storeId: '',
        description: '',
        uuid: '',
        countdownDelayMinutes: 0,
      },
      errors: {}
    }
  },
  computed: {
    isSelectedSocketType() {
      const selected = this.deviceTypeOptions.find(type => type.code === this.formData.deviceType)
      return !!(selected && selected.socketType)
    },
    isSelectedComputerType() {
      const selected = this.deviceTypeOptions.find(type => type.code === this.formData.deviceType)
      return !!(selected && selected.computerType)
    },
    filteredSystemUuidList() {
      const keyword = String(this.uuidSearchKeyword || '').trim().toLowerCase()
      if (!keyword) {
        return this.systemUuidList
      }
      const matched = this.systemUuidList.filter(item => String(item.uuid || '').toLowerCase().includes(keyword))
      if (this.formData.uuid && !matched.some(item => item.uuid === this.formData.uuid)) {
        const selected = this.systemUuidList.find(item => item.uuid === this.formData.uuid)
        if (selected) {
          return [selected, ...matched]
        }
      }
      return matched
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        this.loadSystemUuids()
        this.loadDeviceTypeOptions()
      }
    },
    'formData.storeId'(newVal) {
      if (this.visible) {
        this.loadDeviceTypeOptions(newVal)
      }
    }
  },
  methods: {
    resetForm() {
      this.formData = {
        deviceCode: '',
        deviceName: '',
        deviceType: '1',
        storeId: '',
        description: '',
        uuid: '',
        countdownDelayMinutes: 0,
      }
      this.errors = {}
      this.loading = false
      this.uuidSearchKeyword = ''
      this.deviceTypeOptions = []
    },
    
    async loadSystemUuids() {
      try {
        const response = await request.get('/api/devices/getAllSystemUuids')
        
        if (response.data.success && response.data.code === 200) {
          this.systemUuidList = response.data.data || []
        } else {
          console.error('获取系统UUID列表失败:', response.data.message)
          this.systemUuidList = []
        }
      } catch (error) {
        console.error('获取系统UUID列表失败:', error)
        this.systemUuidList = []
      }
    },

    async loadDeviceTypeOptions(storeId = this.formData.storeId) {
      this.loadingDeviceTypes = true
      try {
        const response = await request.get('/api/devices/device-types', {
          params: {
            storeId: storeId || undefined
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.deviceTypeOptions = response.data.data || []
          if (!this.deviceTypeOptions.some(type => type.code === this.formData.deviceType)) {
            this.formData.deviceType = this.deviceTypeOptions[0]?.code || '1'
          }
        } else {
          throw new Error(response.data.message || '获取设备类型失败')
        }
      } catch (error) {
        console.error('获取设备类型失败:', error)
        this.deviceTypeOptions = [
          { code: '1', name: '电脑', computerType: true, socketType: false },
          { code: '2', name: '智能插座', computerType: false, socketType: true },
          { code: '3', name: '直接扣费项目设备', computerType: false, socketType: false }
        ]
      } finally {
        this.loadingDeviceTypes = false
      }
    },

    addDeviceTypeOption() {
      const nextCode = this.getNextDeviceTypeCode()
      this.deviceTypeOptions.push({
        code: nextCode,
        name: `新设备类型${nextCode}`,
        computerType: true,
        socketType: false
      })
      this.formData.deviceType = nextCode
    },

    removeDeviceTypeOption(index) {
      const removed = this.deviceTypeOptions[index]
      if (!removed || !confirm(`确定删除设备类型“${removed.name}”吗？已使用该类型的设备不会自动改类型。`)) {
        return
      }
      this.deviceTypeOptions.splice(index, 1)
      if (!this.deviceTypeOptions.some(type => type.code === this.formData.deviceType)) {
        this.formData.deviceType = this.deviceTypeOptions[0]?.code || '1'
      }
    },

    getNextDeviceTypeCode() {
      const usedCodes = this.deviceTypeOptions
        .map(type => Number(type.code))
        .filter(code => Number.isFinite(code))
      return String((usedCodes.length ? Math.max(...usedCodes) : 3) + 1)
    },

    setComputerType(type, checked) {
      type.computerType = checked
      if (checked) {
        type.socketType = false
      }
    },

    setSocketType(type, checked) {
      type.socketType = checked
      if (checked) {
        type.computerType = false
      }
    },

    selectUuid(uuid) {
      this.formData.uuid = uuid
      this.uuidSearchKeyword = uuid
    },

    clearSelectedUuid() {
      this.formData.uuid = ''
    },

    async saveDeviceTypeOptions() {
      if (!this.formData.storeId) {
        alert('请先选择所属门店')
        return false
      }

      const options = this.deviceTypeOptions
        .map(type => ({
          code: String(type.code || '').trim(),
          name: String(type.name || '').trim(),
          computerType: Boolean(type.computerType),
          socketType: Boolean(type.socketType)
        }))
        .filter(type => type.name)

      if (options.length === 0) {
        alert('至少保留一个设备类型')
        return false
      }

      this.loadingDeviceTypes = true
      try {
        const response = await request.put('/api/devices/device-types', options, {
          params: {
            storeId: Number(this.formData.storeId)
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.deviceTypeOptions = response.data.data || options
          if (!this.deviceTypeOptions.some(type => type.code === this.formData.deviceType)) {
            this.formData.deviceType = this.deviceTypeOptions[0]?.code || '1'
          }
          return true
        } else {
          throw new Error(response.data.message || '保存设备类型失败')
        }
      } catch (error) {
        console.error('保存设备类型失败:', error)
        alert(error.response?.data?.message || error.message || '保存设备类型失败')
        return false
      } finally {
        this.loadingDeviceTypes = false
      }
    },
    
    validateForm() {
      this.errors = {}
      let isValid = true

      if (!this.formData.deviceCode.trim()) {
        this.errors.deviceCode = '请输入设备编码'
        isValid = false
      }
      
      if (!this.formData.deviceName.trim()) {
        this.errors.deviceName = '请输入设备名称'
        isValid = false
      }
      
      if (!this.formData.storeId) {
        this.errors.storeId = '请选择所属门店'
        isValid = false
      }

      return isValid
    },
    
    async handleSubmit() {
      if (!this.validateForm()) {
        return
      }
      
      this.loading = true
      
      try {
        const savedTypes = await this.saveDeviceTypeOptions()
        if (!savedTypes) {
          return
        }

        const requestData = {
          deviceCode: this.formData.deviceCode.trim(),
          deviceName: this.formData.deviceName.trim(),
          deviceType: this.formData.deviceType,
          storeId: Number(this.formData.storeId),
          description: this.formData.description.trim(),
          uuid: this.isSelectedComputerType ? (this.formData.uuid || null) : null,
          countdownDelayMinutes: this.formData.deviceType === '3' ? 0 : (Number(this.formData.countdownDelayMinutes) || 0),
        }
        
        const response = await request.post('/api/devices/createDevice', requestData)
        
        if (response.data.success && response.data.code === 200) {
          this.$emit('success', response.data.data)
          this.handleClose()
        } else {
          throw new Error(response.data.message || '新增设备失败')
        }
        
      } catch (error) {
        console.error('新增设备失败:', error)
        let errorMessage = '新增设备失败'
        
        if (error.response) {
          errorMessage = error.response.data.message || `服务器错误: ${error.response.status}`
        } else if (error.request) {
          errorMessage = '网络连接失败，请检查网络或服务器状态'
        } else {
          errorMessage = error.message || '新增设备失败，请重试'
        }
        
        alert(errorMessage)
      } finally {
        this.loading = false
      }
    },
    
    handleClose() {
      this.$emit('close')
    },
    
    handleOverlayClick() {
      if (!this.loading) {
        this.handleClose()
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #666;
}

.modal-body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.device-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #ff4d4f;
}

.form-input, .form-textarea, .form-select {
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  font-family: inherit;
}

.form-input:focus, .form-textarea:focus, .form-select:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.form-input.error, .form-textarea.error, .form-select.error {
  border-color: #ff4d4f;
}

.form-select {
  background-color: white;
  cursor: pointer;
}

.form-select option {
  padding: 8px;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
}

.form-help {
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 6px;
}

.topic-help {
  padding: 12px 14px;
  border-radius: 6px;
  background: #f6fbff;
  border: 1px solid #d6ecff;
  color: #35526b;
  font-size: 13px;
  line-height: 1.6;
}

.topic-help p {
  margin: 0;
}

.topic-help p + p {
  margin-top: 6px;
}

.device-type-manager {
  padding: 12px;
  border: 1px solid #e8f1ff;
  border-radius: 8px;
  background: #f8fbff;
}

.manager-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: 600;
  color: #333;
}

.type-option-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.type-name-input {
  flex: 1;
}

.type-kind-check {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 10px;
  border: 1px solid #91d5ff;
  border-radius: 6px;
  color: #096dd9;
  background: #e6f7ff;
  white-space: nowrap;
}

.type-kind-check.socket {
  border-color: #d3adf7;
  color: #722ed1;
  background: #f9f0ff;
}

.type-kind-check.active {
  font-weight: 600;
}

.type-kind-check.muted {
  opacity: 0.45;
}

.uuid-search-input {
  width: 100%;
}

.selected-uuid {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  color: #135200;
  background: #f6ffed;
  word-break: break-all;
}

.selected-uuid button {
  flex: none;
  border: none;
  color: #ff4d4f;
  background: transparent;
  cursor: pointer;
}

.uuid-result-list {
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
}

.uuid-result-list.error {
  border-color: #ff4d4f;
}

.uuid-result-item {
  display: block;
  width: 100%;
  padding: 9px 12px;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
  background: #fff;
  text-align: left;
  cursor: pointer;
  word-break: break-all;
}

.uuid-result-item:hover,
.uuid-result-item.active {
  color: #1890ff;
  background: #e6f7ff;
}

.uuid-empty {
  padding: 10px 12px;
  color: #999;
}

.small-btn, .save-type-btn, .remove-type-btn {
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s ease;
}

.small-btn {
  padding: 6px 10px;
  color: #1890ff;
  background: #e6f7ff;
}

.save-type-btn {
  padding: 8px 12px;
  color: #fff;
  background: #1890ff;
}

.remove-type-btn {
  padding: 8px 10px;
  color: #ff4d4f;
  background: #fff1f0;
}

.small-btn:disabled, .save-type-btn:disabled {
  cursor: not-allowed;
  color: #999;
  background: #f5f5f5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.cancel-btn, .submit-btn {
  padding: 8px 20px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.cancel-btn {
  background: white;
  color: #666;
}

.cancel-btn:hover {
  background: #f5f5f5;
  border-color: #40a9ff;
}

.submit-btn {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.submit-btn:hover:not(:disabled) {
  background: #40a9ff;
  border-color: #40a9ff;
}

.submit-btn:disabled {
  background: #d9d9d9;
  border-color: #d9d9d9;
  cursor: not-allowed;
  color: #999;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 20px;
  }
  
  .modal-header, .modal-body, .modal-footer {
    padding: 16px;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .cancel-btn, .submit-btn {
    width: 100%;
  }
}
</style>
